import { Chip, Fab, Grid, Icon, ListItemIcon, ListItemText, MenuItem } from '@mui/material';
import ErrorBoundary from 'app/components/ErrorBoundary';
import { RoleModal } from 'app/containers/Articles/ManagePermissionsSettings/components/RoleModal';
import { translations } from 'locales/i18n';
import { useRef, FunctionComponent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from 'app/components/Modal';

import './styles/RoleItem.scss';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';

import { useMutation } from '@apollo/client';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { useDispatch } from 'react-redux';
import ContextMenu from 'app/components/ContextMenu';
import { Menu } from '@mui/icons-material';
import { GraphQLErrorHelper } from 'utils/GraphQLErrorHelper';

import DELETE_MUTATION from 'gql/mutations/PermissionProvider/deleteRole.gql';
interface Props {
  role: any;
}

export const RoleItem: FunctionComponent<Props> = ({ role }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const anchorRef = useRef(null);

  const [deleteRole] = useMutation(DELETE_MUTATION);

  const handleDelete = async () => {
    try {
      await deleteRole({
        variables: {
          id: role.id
        },
        refetchQueries: ['GetRoles', 'GetPermissions']
      });
      dispatch(
        AlertActions.success(
          t(translations.pages.managePermissionsSettings.alertsRole.deleteSuccess())
        )
      );
    } catch (err) {
      const gqlErrorHelper = new GraphQLErrorHelper(err);
      dispatch(
        AlertActions.error(
          t(translations.pages.managePermissionsSettings.alertsRole.deleteFailed(), {
            cause: gqlErrorHelper.getBackendErrorMessage()
          })
        )
      );
    }
  };

  const renderGroupEntitlements = (role: any) => {
    return (
      <>
        {role.groupEntitlements.length === 0 ? null : (
          <>
            {role.groupEntitlements.length > 3 ? (
              <Grid container>
                {role.groupEntitlements.slice(0, 3).map(ent => (
                  <Chip label={ent.entitlement} className="role__roles__item" />
                ))}
                <Chip
                  label={`+${role.groupEntitlements.length - 3}`}
                  className="role__roles__item"
                />
              </Grid>
            ) : (
              <Grid container>
                {role.groupEntitlements.map(ent => (
                  <Chip label={ent.entitlement} className="role__roles__item" />
                ))}
              </Grid>
            )}
          </>
        )}
      </>
    );
  };

  const ActionButtons = () => {
    return (
      <ContextMenu open={menuOpen} setOpen={setMenuOpen} anchorEl={anchorRef.current}>
        <MenuItem className="ioCourse-item__actions__container">
          <Grid
            container
            className="ioCourse-item__actions__container"
            onClick={() => {
              setEditModalOpen(true);
              setMenuOpen(false);
            }}
          >
            <Grid item xs={3}>
              <ListItemIcon>
                <Icon
                  className="far fa-edit"
                  style={{
                    width: 'auto',
                    color: 'lightblue'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText primary={t(translations.common.edit())} />
            </Grid>
          </Grid>
        </MenuItem>
        <MenuItem className="ioCourse-item__actions__container">
          <Grid
            container
            className="ioCourse-item__actions__container"
            onClick={() => {
              setDeleteModalOpen(true);
              setMenuOpen(false);
            }}
          >
            <Grid item xs={3}>
              <ListItemIcon>
                <Icon
                  className="far fa-trash-alt"
                  style={{
                    width: 'auto',
                    color: 'red'
                  }}
                />
              </ListItemIcon>
            </Grid>
            <Grid item xs={9}>
              <ListItemText primary={t(translations.common.delete())} />
            </Grid>
          </Grid>
        </MenuItem>
      </ContextMenu>
    );
  };

  return (
    <Grid
      container
      className="role"
      style={{
        backgroundColor: !role.active ? 'lightgrey' : undefined
      }}
    >
      <ErrorBoundary boundaryTag="roleDetailBoundary">
        <Modal
          open={editModalOpen}
          onClose={(event, reason) => {
            if (reason && reason === 'backdropClick') return;
            setEditModalOpen(false);
          }}
          headline={t(translations.pages.managePermissionsSettings.roleEditing())}
          onCancel={() => {
            setEditModalOpen(false);
          }}
          disabledCancel={disabledCancel}
        >
          <RoleModal
            role={{ ...role }}
            onCancel={() => setEditModalOpen(false)}
            closeHandler={() => {
              setEditModalOpen(false);
            }}
            setDisabledCancel={setDisabledCancel}
          />
        </Modal>
      </ErrorBoundary>
      <Grid container>
        <Grid item xs={1} className="role__id">
          {role.id}
        </Grid>
        <Grid item xs={1} className="role__icon">
          <Icon
            className={'role__icon__image ' + role.icon}
            style={{
              color: !role.active ? 'red' : 'black'
            }}
          />
        </Grid>
        <Grid item xs={3} className="role__name">
          {role.name}
        </Grid>
        <Grid item xs={6} className="role__roles">
          {renderGroupEntitlements(role)}
        </Grid>
        <Grid item xs={1} className="role__actions">
          <Fab size="small" className="action" onClick={() => setMenuOpen(true)} ref={anchorRef}>
            <Menu />
          </Fab>
          <ActionButtons />
        </Grid>
      </Grid>
      <ConfirmationDialog
        open={deleteModalOpen}
        title={t(translations.pages.managePermissionsSettings.deleteModalRole.title())}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
        onConfirm={() => {
          handleDelete();
          setDeleteModalOpen(false);
        }}
      >
        <div>{t(translations.pages.managePermissionsSettings.deleteModalRole.message())}</div>
      </ConfirmationDialog>
    </Grid>
  );
};
